import {useDiffEntriesWithFilter, useFilteredDiffSummaries} from '../stores/selectors'
import {useSelectedDiffHash} from '@github-ui/diffs/use-selected-diff-hash'
import {updateURLHash} from '@github-ui/diffs/document-hash-helpers'
import {addUrlToHistoryStack} from '@github-ui/history'
import {useCallback, useMemo} from 'react'

type UseDiffNavigation = {
  /** Function to navigate to the previous file */
  navigateToPrevious: () => void
  /** Function to navigate to the next file */
  navigateToNext: () => void
  /** The currently selected path digest */
  currentPathDigest: string | undefined
  /** The current index in the diff entries array */
  currentIndex?: number
}

/**
 * Hook that provides navigation functionality for navigating files in a pull request.
 *
 * Navigation should only operate on files that are visible in the file tree. The
 * store exposes both the full progressive entries and a filtered list derived from
 * the current file filter state; prefer the filtered list so "Next/Previous" skips
 * hidden files.
 *
 * @returns UseDiffNavigation object containing navigation functions and state
 */
export function useDiffNavigation(): UseDiffNavigation {
  // Get the filtered summaries and use the specialized selector to obtain the
  // progressive entries that correspond to the currently visible files.
  const filteredDiffSummaries = useFilteredDiffSummaries()
  const diffEntries = useDiffEntriesWithFilter(filteredDiffSummaries)

  const {selectedPathDigest} = useSelectedDiffHash()

  // Find the current index in the diff entries array
  const currentIndex = useMemo(() => {
    return selectedPathDigest ? diffEntries.findIndex(entry => entry.pathDigest === selectedPathDigest) : 0
  }, [diffEntries, selectedPathDigest])

  const navigate = useCallback((pathDigest: string) => {
    updateURLHash(`diff-${pathDigest}`)
    addUrlToHistoryStack(window.location.href)
  }, [])

  const navigateToPrevious = useCallback(() => {
    if (diffEntries.length > 0) {
      let previousIndex: number
      if (currentIndex <= 0) {
        // Wrap to the last item
        previousIndex = diffEntries.length - 1
      } else {
        previousIndex = currentIndex - 1
      }
      const entry = diffEntries[previousIndex]
      if (entry) {
        navigate(entry.pathDigest)
      }
    }
  }, [currentIndex, diffEntries, navigate])

  const navigateToNext = useCallback(() => {
    if (diffEntries.length > 0) {
      let nextIndex: number
      if (currentIndex >= diffEntries.length - 1 || currentIndex < 0) {
        // Wrap to the first item
        nextIndex = 0
      } else {
        nextIndex = currentIndex + 1
      }
      const entry = diffEntries[nextIndex]
      if (entry) {
        navigate(entry.pathDigest)
      }
    }
  }, [currentIndex, diffEntries, navigate])

  return {
    navigateToPrevious,
    navigateToNext,
    currentPathDigest: selectedPathDigest,
    currentIndex: currentIndex >= 0 ? currentIndex : undefined,
  }
}
